import { Building2 } from 'lucide-react'

import { ORG_LOGO_PLACEHOLDER_SURFACE_CLASS } from '@/lib/initials-avatar-style'
import { cn } from '@/lib/utils'

export type EntityLogoSize = 'hero' | 'editor' | 'thumb'

const SIZE_CLASS: Record<EntityLogoSize, string> = {
  hero: 'size-[120px]',
  editor: 'size-[120px]',
  thumb: 'size-12',
}

const PLACEHOLDER_ICON_CLASS: Record<EntityLogoSize, string> = {
  hero: 'size-10',
  editor: 'size-10',
  thumb: 'size-5',
}

export type EntityLogoProps = {
  src?: string | null
  alt?: string
  size?: EntityLogoSize
  className?: string
  /** 6px card ring — identity heroes (private + public share) and matching editors only. */
  framed?: boolean
}

/**
 * Squared organisation logo with rounded corners (`object-contain`).
 * Avatar stays round via {@link InitialsAvatar}; logo stays squared.
 */
export function EntityLogo({
  src,
  alt = '',
  size = 'hero',
  className,
  framed = false,
}: EntityLogoProps) {
  const normalizedSrc = src?.trim() || null

  return (
    <div
      className={cn(
        'shrink-0 overflow-hidden rounded-xl bg-transparent',
        SIZE_CLASS[size],
        framed && 'border-[6px] border-card',
        normalizedSrc && 'bg-muted/40',
        className,
      )}
    >
      {normalizedSrc ? (
        <img
          src={normalizedSrc}
          alt={alt}
          className="size-full object-contain outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10"
          loading={size === 'thumb' ? 'lazy' : undefined}
          decoding={size === 'thumb' ? 'async' : undefined}
        />
      ) : (
        <div
          className={cn(
            'flex size-full items-center justify-center',
            // Quiet placeholder on list thumbs; brand fill reserved for identity heroes/editors.
            size === 'thumb' ? 'bg-muted' : ORG_LOGO_PLACEHOLDER_SURFACE_CLASS,
          )}
        >
          <Building2
            className={cn(
              PLACEHOLDER_ICON_CLASS[size],
              size === 'thumb' ? 'text-muted-foreground' : 'text-white',
            )}
            aria-hidden
          />
        </div>
      )}
    </div>
  )
}
